/**
 * Картинки по прямой ссылке: сервер кладёт URL в Photos.full_url и не скачивает файл.
 * В multipart поле photo_urls — JSON-массив строк. Файлы и ссылки считаются вместе.
 */

import { PHOTOS_COUNT } from './constants';

export function isPhotoUrl(value) {
  const raw = String(value || '').trim();
  if (!raw) return false;
  try {
    const url = new URL(raw);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch (e) {
    return false;
  }
}

export function normalizePhotoUrls(list) {
  const seen = new Set();
  const result = [];
  const source = Array.isArray(list) ? list : [];

  source.forEach((item) => {
    const value = String(item || '').trim();
    if (!isPhotoUrl(value) || seen.has(value)) return;
    seen.add(value);
    result.push(value);
  });

  return result;
}

/** Смешанный список карусели: файлы { file } и ссылки { url } или строки. */
export function splitCarouselPhotos(photos = []) {
  const files = [];
  const urls = [];

  (photos || []).forEach((photo) => {
    if (typeof photo === 'string') {
      urls.push(photo);
      return;
    }
    if (!photo || typeof photo !== 'object') return;
    if (photo.url) {
      urls.push(photo.url);
    } else if (photo.file || photo instanceof Blob) {
      files.push(photo);
    }
  });

  return { files, urls: normalizePhotoUrls(urls) };
}

function makePhotoId() {
  return `photo_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Единый список карусели для форм и массового создания. */
export function buildCarouselPhotos({ files = [], urls = [] } = {}) {
  const fileItems = (files || []).map((item) => {
    if (!item) return null;
    if (item.file || item instanceof Blob) {
      const file = item.file || item;
      return {
        id: item.id || makePhotoId(),
        file,
        uri: item.uri || URL.createObjectURL(file),
      };
    }
    return null;
  }).filter(Boolean);

  const urlItems = normalizePhotoUrls(urls).map((url) => ({
    id: url,
    url,
    uri: url,
  }));

  return [...fileItems, ...urlItems].slice(0, PHOTOS_COUNT.carouselMax);
}

export function countCarouselPhotos(photos = []) {
  const { files, urls } = splitCarouselPhotos(photos);
  return files.length + urls.length;
}

/**
 * Фото черновика массового создания:
 * если в режиме заданы файлы/ссылки — они принудительны;
 * иначе берём photo_urls из ответа модели.
 */
export function photosForBulkDraft(raw, config, warnings = []) {
  const configPhotos = Array.isArray(config?.photos) ? config.photos : [];
  const { files: configFiles, urls: configUrlsFromPhotos } = splitCarouselPhotos(configPhotos);
  const legacyUrls = normalizePhotoUrls(config?.photoUrls || []);
  const forcedUrls = configUrlsFromPhotos.length ? configUrlsFromPhotos : legacyUrls;
  const hasForced = Boolean(configFiles.length || forcedUrls.length);

  const rawList = Array.isArray(raw?.photo_urls) ? raw.photo_urls : [];
  const fromRaw = normalizePhotoUrls(rawList);

  if (!hasForced && rawList.length && !fromRaw.length) {
    warnings.push('ссылки photo_urls из файла не похожи на http(s) — добавьте фото вручную');
  }

  const urls = hasForced ? forcedUrls : fromRaw;
  if (configFiles.length + urls.length > PHOTOS_COUNT.carouselMax) {
    warnings.push(`фото больше ${PHOTOS_COUNT.carouselMax} — взяты первые ${PHOTOS_COUNT.carouselMax}`);
  }

  return buildCarouselPhotos({ files: configFiles, urls });
}
